import React from 'react';
import { ArrowRight, Anchor, MapPin, Shield, CheckCircle, Waves, Clock } from 'lucide-react';
import SEO from '../components/SEO';

const BoatStorageLake = () => {
  const structuredData = {
    '@context': 'https://schema.org',
    '@type': 'SelfStorage',
    name: 'V&D Storage – Boat Storage near Richland Chambers Lake',
    url: 'https://www.vdstoragestreetman.com/boat-storage-richland-chambers-lake',
    telephone: '(800) 768-7640',
    address: {
      '@type': 'PostalAddress',
      streetAddress: 'FM 416',
      addressLocality: 'Streetman',
      addressRegion: 'TX',
      postalCode: '75859',
      addressCountry: 'US'
    },
    geo: {
      '@type': 'GeoCoordinates',
      latitude: 31.8793,
      longitude: -96.3133
    },
    description: 'Boat storage near Richland Chambers Lake on FM 416. Drive-up outdoor parking on concrete driveways. Secure fenced facility with electronic gate access.',
    priceRange: '$'
  };

  const benefits = [
    'Minutes from Richland Chambers Lake boat ramps',
    'Wide concrete driveways — no mud, no ruts, ever',
    'Electronic gate access, 6 AM – 10 PM daily',
    'Fully fenced with 24/7 video surveillance',
    'Room for large bass boats, pontoons, and ski boats with trailers',
    'No HOA parking restrictions to worry about',
    'Affordable monthly rates — far less than marina storage',
    'Rent online in minutes — no office visit required'
  ];

  const boatTypes = [
    { icon: <Anchor className="h-8 w-8 text-green-600" />, label: 'Bass Boats' },
    { icon: <Waves className="h-8 w-8 text-green-600" />, label: 'Pontoon Boats' },
    { icon: <Waves className="h-8 w-8 text-green-600" />, label: 'Ski Boats' },
    { icon: <Anchor className="h-8 w-8 text-green-600" />, label: 'Jet Skis & PWC' }
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      <SEO
        title="Boat Storage Near Richland Chambers Lake | V&D Storage"
        description="Secure outdoor boat storage near Richland Chambers Lake on FM 416. Concrete driveways, electronic gate, affordable rates. Bass boats, pontoons, jet skis welcome. Rent online."
        canonical="/boat-storage-richland-chambers-lake"
        structuredData={structuredData}
      />

      {/* Header */}
      <section className="bg-green-600 text-white py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl md:text-5xl font-bold mb-4" data-testid="boat-storage-lake-title">
            Boat Storage near Richland Chambers Lake
          </h1>
          <p className="text-xl text-green-100 max-w-3xl mx-auto">
            Secure outdoor boat storage on FM 416 — minutes from the lake launch ramps
          </p>
        </div>
      </section>

      {/* Intro */}
      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="prose prose-lg max-w-none">
            <p className="text-xl text-gray-700 leading-relaxed mb-6">
              Richland Chambers Lake is one of the largest reservoirs in Texas — and keeping your boat close means more time on the water. V&D Storage is just minutes from the Richland Chambers Lake boat ramps, with fully concrete driveways to every parking space so you can pull in and out with ease no matter the weather.
            </p>
            <p className="text-lg text-gray-700 leading-relaxed">
              Stop hauling your boat to and from home every weekend. Store it here, grab it on the way to the ramp, and put it back on the way home — it's that simple.
            </p>
          </div>
        </div>
      </section>

      {/* Boat Types */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              We Store All Types of Watercraft
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Wide drive aisles and concrete surfaces accommodate boats of all sizes
            </p>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {boatTypes.map((type, index) => (
              <div key={index} className="bg-white p-6 rounded-xl shadow-md text-center">
                <div className="flex justify-center mb-3">{type.icon}</div>
                <p className="text-gray-800 font-semibold">{type.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Why Lake Visitors Choose V&D Storage
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            <div className="bg-gray-50 p-8 rounded-xl text-center">
              <MapPin className="h-12 w-12 text-green-600 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-gray-900 mb-3">Minutes from the Ramps</h3>
              <p className="text-gray-600">
                Located on FM 416 — the most direct route to Richland Chambers Lake boat launches.
              </p>
            </div>
            <div className="bg-gray-50 p-8 rounded-xl text-center">
              <Waves className="h-12 w-12 text-green-600 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-gray-900 mb-3">Concrete Driveways</h3>
              <p className="text-gray-600">
                Every space has a fully concrete driveway — no mud, no ruts, no getting stuck. Works great for trailers.
              </p>
            </div>
            <div className="bg-gray-50 p-8 rounded-xl text-center">
              <Shield className="h-12 w-12 text-green-600 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-gray-900 mb-3">Secure & Monitored</h3>
              <p className="text-gray-600">
                Fully fenced perimeter with electronic gate access and 24/7 video surveillance.
              </p>
            </div>
            <div className="bg-gray-50 p-8 rounded-xl text-center">
              <Clock className="h-12 w-12 text-green-600 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-gray-900 mb-3">Extended Access Hours</h3>
              <p className="text-gray-600">
                Gate access from 6:00 AM to 10:00 PM every day — get your boat early for a full day on the water.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Benefits List */}
      <section className="py-16 bg-green-50 border-t border-b border-green-100">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-8 text-center">
            Everything You Need in a Boat Storage Facility
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {benefits.map((benefit, index) => (
              <div key={index} className="flex items-start space-x-3">
                <CheckCircle className="h-6 w-6 text-green-600 flex-shrink-0 mt-0.5" />
                <span className="text-lg text-gray-700">{benefit}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* About the Lake */}
      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-6">About Richland Chambers Lake</h2>
          <p className="text-lg text-gray-700 leading-relaxed mb-4">
            Richland Chambers Reservoir is one of Texas's premier freshwater fishing and recreation lakes, spanning over 41,000 acres in Navarro and Freestone counties. Known for its excellent bass, catfish, and crappie fishing, the lake draws anglers and boaters year-round from the Dallas–Fort Worth metroplex and beyond.
          </p>
          <p className="text-lg text-gray-700 leading-relaxed">
            We're on the western edge of the lake, making V&D Storage the ideal home base for your watercraft — close to the water, on a paved highway, with the easiest access in the area.
          </p>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-gray-900 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold mb-4">Reserve Your Boat Parking Space Today</h2>
          <p className="text-xl text-gray-300 mb-8">
            Check availability online and rent in minutes — no office visit required.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href="https://vdstoragestreetman.storageunitsoftware.com/pages/rent"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center px-8 py-3 text-lg font-semibold text-white bg-green-600 rounded-lg hover:bg-green-700 transition-colors shadow-lg btn-primary"
              data-testid="rent-now-button"
            >
              Rent Online Now
              <ArrowRight className="ml-2 h-5 w-5" />
            </a>
            <a
              href="/contact"
              className="inline-flex items-center px-8 py-3 text-lg font-semibold text-gray-900 bg-white rounded-lg hover:bg-gray-100 transition-colors shadow-lg"
              data-testid="contact-button"
            >
              Contact Us
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default BoatStorageLake;
